import { AspectRatio, Box, Typography } from "@mui/joy";
import mockBackground from "../../../assets/background.jpg";
import { ILotto, ILottoImage } from "../../../models";
import { useMemo, useState } from "react";
import {
  BASE_URL,
  IMAGE_LABEL_FORMULA_IT,
  IMAGE_LABEL_FORMULA_EN,
} from "../../../config";
import { getImageName } from "../../../lib/functions";
import CarouselDialog from "./dialogs/carousel/carouselDialog";
import { useTranslation } from "react-i18next";

const IMAGE_URL = `${BASE_URL}image/`;

export interface ILottoImagesProps {
  lotto: ILotto | null;
}

export default function LottoImages({ lotto }: ILottoImagesProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
  const cover: ILottoImage | undefined = useMemo(
    () => lotto?.images?.find((image) => image.isCover) ?? lotto?.images?.[0],
    [lotto]
  );
  const imageLabelFormula = useMemo(
    () =>
      i18n.language === "en" ? IMAGE_LABEL_FORMULA_EN : IMAGE_LABEL_FORMULA_IT,
    [i18n]
  );
  const otherImages: ILottoImage[] = useMemo(() => {
    return (
      lotto?.images
        ?.filter((image) => !image.isCover)
        .sort((a, b) => a.slab - b.slab) ?? []
    );
  }, [lotto]);

  const onView = (imageId?: number) => {
    if (!imageId) return;
    setSelectedImageId(imageId);
    setIsOpen(true);
  };
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",

        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          "@media screen and (min-width: 1024px)": {
            position: "absolute",
            overflow: "auto",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            pt: 4,
            "@media screen and (min-width: 1024px)": { pr: 16 },
          }}
        >
          <Box
            sx={{ flex: 1, "@media screen and (min-width: 1024px)": { pl: 2 } }}
          >
            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                transition: "all 300ms ease",
                img: { transition: "all 300ms ease" },
                "&:hover": {
                  transform: "translateY(-5px)",
                  ".item-desc": {
                    opacity: "1",
                  },
                  img: {
                    transform: "scale(1.1)",
                  },
                },
              }}
            >
              <AspectRatio objectFit="contain" sx={{ p: 1 }}>
                <img
                  src={cover ? `${IMAGE_URL}${cover.mdPath}` : mockBackground}
                  alt="cover"
                />
              </AspectRatio>
              <Box
                className="item-desc"
                sx={{
                  top: 0,
                  left: 0,
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  opacity: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  transition: "all 300ms ease",
                }}
                onClick={() => onView(cover?.id)}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    mb: 2,
                    display: "none",
                    padding: 1,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    "@media screen and (min-width: 1024px)": {
                      display: "block",
                    },
                  }}
                  level="h2"
                >
                  {lotto && cover
                    ? getImageName(cover, lotto, imageLabelFormula)
                    : ""}
                </Typography>
                <Typography
                  sx={{
                    color: "#fff",
                    mb: 2,
                    padding: 1,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    "@media screen and (min-width: 1024px)": {
                      display: "none",
                    },
                  }}
                  level="title-md"
                >
                  {lotto && cover
                    ? getImageName(cover, lotto, imageLabelFormula)
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                pb: 20,
                "@media screen and (min-width: 1024px)": {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "center",
                },
              }}
            >
              {otherImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    transition: "all 300ms ease",
                    img: { transition: "all 300ms ease" },
                    "&:hover": {
                      transform: "translateY(-5px)",
                      ".item-desc": {
                        opacity: "1",
                      },
                      img: {
                        transform: "scale(1.1)",
                      },
                    },
                  }}
                >
                  <AspectRatio
                    sx={{
                      p: 1,
                      "@media screen and (min-width: 1024px)": {
                        width: "400px",
                      },
                    }}
                    objectFit="contain"
                  >
                    <img
                      src={`${IMAGE_URL}${image.mdPath}`}
                      alt={`image_${index}`}
                    />
                  </AspectRatio>
                  <Box
                    className="item-desc"
                    sx={{
                      top: 0,
                      left: 0,
                      position: "absolute",
                      height: "100%",
                      width: "100%",
                      opacity: 0,
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      transition: "all 300ms ease",
                    }}
                    onClick={() => onView(image.id)}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        mb: 2,
                        padding: 1,
                        backgroundColor: "rgba(0,0,0,0.4)",
                      }}
                      level="title-md"
                    >
                      {lotto
                        ? getImageName(image, lotto, imageLabelFormula)
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      {lotto && (
        <CarouselDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          selectedImageId={selectedImageId}
          lotto={lotto}
        ></CarouselDialog>
      )}
    </Box>
  );
}
